import { jsx as _jsx } from "react/jsx-runtime";
/**
 * TextInputModal Component
 * A UI component responsible for collecting text input, implemented as a fully controlled component.
 */
import { useState, useEffect, useCallback } from 'react';
import { TextInputModalUI } from '../TextInputModalUI/index.js';
/**
 * Text Input Modal Component.
 * Implemented as a fully controlled component.
 */
export const TextInputModal = ({ isOpen, onClose, initialValue = '', onSubmit, title = 'Enter Text to Translate', submitButtonText = 'Submit', cancelButtonText = 'Cancel', placeholder = 'Enter or paste text to savor multilingual reading', closeOnBackdropClick = false, currentModelId, availableModels, currentTargetLanguage, availableSourceLanguages, onModelChange, onSourceLanguageChange, onTargetLanguageChange, }) => {
    const [text, setText] = useState(initialValue);
    const [error, setError] = useState(null);
    // DEBUG: Log props changes (especially model and target language)
    useEffect(() => { }, [currentModelId, currentTargetLanguage, availableModels]);
    useEffect(() => {
        if (isOpen) {
            setText(initialValue);
            setError(null);
        }
    }, [isOpen, initialValue]);
    const handleTextChange = useCallback((newText) => {
        setText(newText);
        if (error)
            setError(null);
    }, [error]);
    // Callbacks now directly use props
    const handleModelChange = useCallback((newModel) => {
        onModelChange(newModel);
        if (error)
            setError(null);
    }, [onModelChange, error]);
    const handleSourceLanguageChange = useCallback(async (newLanguage) => {
        onSourceLanguageChange(newLanguage);
        if (error)
            setError(null);
    }, [onSourceLanguageChange, error]);
    const handleTargetLanguageChange = useCallback(async (newLanguage) => {
        onTargetLanguageChange(newLanguage);
        if (error)
            setError(null);
    }, [onTargetLanguageChange, error]);
    const handleSubmit = useCallback(() => {
        if (!text.trim()) {
            setError('Please enter text to translate');
            return;
        }
        // Assuming source language is always the first one (e.g., 'auto') or determined by availableSourceLanguages logic
        const sourceLangCode = availableSourceLanguages.length > 0 ? availableSourceLanguages[0].code : 'auto';
        const inputParams = {
            text: text.trim(),
            sourceLanguage: sourceLangCode,
            targetLanguage: currentTargetLanguage,
            model: currentModelId,
        };
        onSubmit(inputParams);
    }, [text, currentModelId, currentTargetLanguage, availableSourceLanguages, onSubmit]);
    if (!isOpen)
        return null;
    // Determine the actual source language value to pass to UI
    // Typically 'auto' if availableSourceLanguages includes it.
    const currentSourceLanguageValue = availableSourceLanguages.find(lang => lang.code === 'auto')
        ? 'auto'
        : availableSourceLanguages.length > 0
            ? availableSourceLanguages[0].code
            : ''; // Fallback, should ideally always have 'auto'
    return (_jsx(TextInputModalUI, { isOpen: isOpen, onClose: onClose, title: title, value: text, onChange: handleTextChange, onSubmit: handleSubmit, error: error || undefined, submitButtonText: submitButtonText, cancelButtonText: cancelButtonText, placeholder: placeholder, closeOnBackdropClick: closeOnBackdropClick, 
        // Model and Language UI props
        model: currentModelId, onModelChange: handleModelChange, models: availableModels, sourceLanguage: currentSourceLanguageValue, targetLanguage: currentTargetLanguage, onSourceLanguageChange: handleSourceLanguageChange, onTargetLanguageChange: handleTargetLanguageChange, showLanguageSelector: true, showModelSelector: true }));
};
export default TextInputModal;
//# sourceMappingURL=TextInputModal.js.map